
import {defineComponent, PropType} from "vue";
import {OverlayRestDto} from "@/api/pharma-cms-content-block/models";
import {OverlayRestDtoModel} from "@/models/api/pharma-cms-content-block/OverlayRestDtoModel";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import ImageOverlayDetail from "@/components/layouts/content-pages/wysiwyg/misc/ImageOverlayDetail.vue";
import MetadataGroupCard from "@/components/layouts/content-pages/wysiwyg/misc/MetadataGroupCard.vue";
import BaseDeleteButton from "@/components/UI/Bootstrap/BaseDeleteButton.vue";

export default defineComponent({
  name: "ImageOverlaysDetail",
  mixins: [ComponentMixin],
  components: {GenericComponentWrapper, ImageOverlayDetail, MetadataGroupCard, BaseDeleteButton},
  emits: ['update:modelValue'],
  props: {
    // image.overlays — null, undefined or empty all mean the group is switched off.
    modelValue: {
      type: Array as PropType<OverlayRestDto[] | null>,
      required: false,
      default: null,
    },
    name: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.persistedDto = this.cloneOverlays(this.modelValue);

    this.componentUI.setReady();
  },
  data() {
    return {
      enabled: false as boolean,
      localDto: [] as OverlayRestDto[],
      // Overlays entered before the group was switched off, so they can be restored.
      lastActiveDto: null as OverlayRestDto[] | null,
      // Reset baseline: the overlays as they are stored in the database.
      persistedDto: null as OverlayRestDto[] | null,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: OverlayRestDto[] | null | undefined) {
        this.enabled = (newValue?.length ?? 0) > 0;
        if (newValue?.length) {
          this.localDto = this.cloneOverlays(newValue) as OverlayRestDto[];
        }
      },
    },
  },
  methods: {
    cloneOverlays(source: OverlayRestDto[] | null | undefined): OverlayRestDto[] | null {
      if (!source?.length) {
        return null;
      }

      return source.map((overlay: OverlayRestDto) => OverlayRestDtoModel.cloneFrom(overlay));
    },
    onEnabledChanged(newValue: boolean): void {
      if (newValue === this.enabled) {
        return;
      }
      this.enabled = newValue;

      if (newValue) {
        this.localDto = this.cloneOverlays(this.lastActiveDto) ?? [OverlayRestDtoModel.createWithDefaults()];

        this.emitModelChanged();
      } else {
        this.lastActiveDto = this.cloneOverlays(this.localDto);

        this.$emit('update:modelValue', null);
      }
    },
    resetToPersisted(): void {
      this.localDto = this.cloneOverlays(this.persistedDto) ?? [OverlayRestDtoModel.createWithDefaults()];

      this.emitModelChanged();
    },
    addOverlay(): void {
      this.localDto.push(OverlayRestDtoModel.createWithDefaults());
      this.emitModelChanged();
    },
    removeOverlay(index: number): void {
      this.localDto.splice(index, 1);

      if (!this.localDto.length) {
        // An empty list means no overlays at all, so the group switches itself off.
        this.enabled = false;
        this.$emit('update:modelValue', null);
        return;
      }

      this.emitModelChanged();
    },
    emitModelChanged(): void {
      // Stacking order follows the order of the list, bottom to top.
      this.localDto.forEach((overlay: OverlayRestDto, index: number) => {
        overlay.sort_weight = index;
      });

      this.$emit('update:modelValue', this.localDto);
    },
  },
});
